<template>
  <div class="border border-[#E5E5E5] rounded-[8px] bg-white">
    <!-- Group Header -->
    <div class="h-[52px] flex items-center gap-[0.75rem] bg-[#F9F9F9] border-b border-[#E5E5E5] rounded-t-[8px]">
      <div class="pl-[0.75rem] w-[50px]">
        <b-form-checkbox
          v-show="!checkboxLoading"
          :checked="groupSelected"
          :indeterminate="groupIndeterminate"
          :disabled="!group.articles || group.articles.length === 0"
          @change="onGroupCheckboxChange"
        />
        <b-spinner
          v-if="checkboxLoading"
          class="!size-[18px]"
          variant="primary"
        />
      </div>
      <div class="flex items-center gap-3">
        <span class="px-[12px] py-[4px] rounded-[6px] bg-[#FFF3EE] text-[#F95E16] text-sm font-medium">
          {{ group.category_name }}
        </span>
        <span class="text-[#F95E16] text-sm font-medium">
          {{ group.total }} Artikel
        </span>
      </div>
    </div>

    <!-- Group Items -->
    <div class="overflow-x-auto">
      <b-table
        v-if="group.articles.length > 0"
        :items="group.articles"
        :fields="config.headers"
        thead-class="d-none"
        tbody-tr-class="flex items-center min-h-[53px] border-b-[1px] border-[#E5E5E5] last:border-b-0"
        class="!mb-[0px]"
        borderless
      >
        <template #cell(checkbox)="{ item, index }">
          <ArticleSelectCheckbox
            :ref="`checkbox-${index}`"
            :checked="!!(selectedMap[item.id] && selectedMap[item.id].value)"
            @change="$emit('toggle-select', { id: item.id, title: item.title, articleIndex: index, value: $event, setLoading: (value, type, status) => setLoadingItem({ value, type, status, id: item.id }) })"
          />
        </template>

        <template #cell(status)="{ item, index }">
          <ArtikelStatusDropdown
            :ref="`status-${index}`"
            :item="item"
            @update-status="$emit('update-status', $event)"
          />
        </template>

        <template #cell(updated_at)="{ item, index }">
          <article-last-update-cell
            :ref="`lastUpdate-${index}`"
            :item="item"
            @delete="$emit('delete', { group, item, index })"
          />
        </template>
      </b-table>
      <!-- Empty State -->
      <div
        v-else
        class="text-center py-[32px] text-[#A0A0A0] text-[14px] bg-[#FFFFFF]"
      >
        {{ config.text.emptyState }}
      </div>
      <div
        v-if="group.has_more"
        class="border-t border-[#E5E5E5] flex items-center justify-center py-[0.70rem]"
      >
        <button
          ref="loadMoreBtn"
          class="py-[0.35rem] px-[24px] text-[#737373] text-center text-[14px] rounded-[8px] hover:bg-[#F4F4F4]/50 transition-colors !flex items-center justify-center gap-[8px]"
          :disabled="loading"
          @click="handleLoadMore(group)"
        >
          <b-spinner
            v-if="loading"
            small
            variant="primary"
          />
          Tampilkan lebih banyak
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ArtikelStatusDropdown from './ArtikelStatusDropdown.vue'
import ArticleLastUpdateCell from './ArticleLastUpdateCell.vue'
import ArticleSelectCheckbox from './ArticleSelectCheckbox.vue'

export default {
  name: 'ArtikelGroup',
  components: {
    ArtikelStatusDropdown,
    ArticleLastUpdateCell,
    ArticleSelectCheckbox,
  },
  props: {
    group: {
      type: Object,
      required: true,
    },
    config: {
      type: Object,
      required: true,
    },
    selectedMap: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      checkboxLoading: false,
    }
  },
  computed: {
    /** True when every article in this group is selected */
    groupSelected() {
      const { articles } = this.group
      if (!articles || !articles.length) return false
      return articles.every(a => !!this.selectedMap[a.id]?.value)
    },
    /** True when some (not all) articles in this group are selected → indeterminate state */
    groupIndeterminate() {
      const { articles } = this.group
      if (!articles || !articles.length) return false
      const selectedCount = articles.filter(a => !!this.selectedMap[a.id]?.value).length
      return selectedCount > 0 && selectedCount < articles.length
    },
  },
  watch: {
    groupSelected: {
      immediate: true,
      handler(newVal) {
        this.$emit('group-selection', newVal, this.group.category_id)
      },
    },
  },
  methods: {
    /** Called only by direct user click on the group header checkbox */
    onGroupCheckboxChange(value) {
      const { articles } = this.group
      if (!articles) return
      const items = articles.map(a => ({
        id: a.id,
        title: a.title,
        setLoading: (v, type, status) => this.setLoadingItem({
          value: v, type, status, id: a.id,
        }),
      }))
      this.$emit('toggle-select-batch', { items, value })
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })
    },
    handleLoadMore(group) {
      this.$emit('load-more', {
        last_id: group?.articles[group?.articles?.length - 1]?.id,
        category_id: group?.category_id,
        set_loading: value => {
          this.loading = value
        },
      })
    },
    /**
     * Updates the loading state of a specific article row based on action type.
     * - type === 'status': updates the status dropdown component and patches the article data
     * - other types: toggles loading on the checkbox, status dropdown, and action buttons
     * @param {{ value: boolean, type: string, status: string|null, index: number }} params
     */
    setLoadingItem({
      value, type, status, id,
    }) {
      const index = this.group.articles.findIndex(a => a.id === id)
      if (index === -1) {
        if (!value) this.checkboxLoading = false
        return
      }

      if (type === 'status') {
        if (this.$refs[`status-${index}`]) {
          this.$refs[`status-${index}`].setLoading(value, status)
        }

        if (status) {
          this.$set(this.group.articles, index, {
            ...this.group.articles[index],
            status,
            updated_at: new Date(),
          })
        }
        return
      }

      if (this.$refs[`checkbox-${index}`]) {
        this.$refs[`checkbox-${index}`].setLoading(value)
      }
      if (this.$refs[`status-${index}`]) {
        this.$refs[`status-${index}`].setDisabled(value)
      }
      if (this.$refs[`lastUpdate-${index}`]) {
        this.$refs[`lastUpdate-${index}`].setDisabled(value)
      }
      this.checkboxLoading = value
    },
    /** Sets the group checkbox loading state */
    setCheckboxLoading(value) {
      this.checkboxLoading = value
    },
  },
}
</script>
